import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import CouponCard from '../coupon/CouponCard';
import { getMedicalCoupons } from '../../../actions/admin/medicalAttribution';

import {
    Alert, Box, Button, CircularProgress, Container, FormControl,
    Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const normalize = value => (value || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const MedicalCoupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [filters, setFilters] = useState({ search: '', doctor_id: '', executive_id: '' });

    useEffect(() => {
        getMedicalCoupons().then(result => {
            setCoupons(result.coupons);
            setFailed(!result.status);
            setLoading(false);
        });
    }, []);

    const doctors = useMemo(() => {
        const values = new Map();
        coupons.forEach(coupon => {
            if (coupon.doctor) values.set(coupon.doctor._id, coupon.doctor);
        });
        return [...values.values()].sort((a, b) =>
            `${a.name?.firstName} ${a.name?.lastName}`.localeCompare(`${b.name?.firstName} ${b.name?.lastName}`)
        );
    }, [coupons]);

    const executives = useMemo(() => {
        const values = new Map();
        coupons.forEach(coupon => {
            if (coupon.registered_by) values.set(coupon.registered_by._id, coupon.registered_by);
        });
        return [...values.values()];
    }, [coupons]);

    const filtered = useMemo(() => coupons.filter(coupon => {
        const text = normalize(filters.search);
        const matchesText = normalize(coupon.name).includes(text) ||
            normalize(coupon.description).includes(text) ||
            normalize(coupon.btl?.uuid).includes(text) ||
            String(coupon.btl?.btl_coupon_program_id || '').includes(text);
        const matchesDoctor = !filters.doctor_id || coupon.doctor?._id === filters.doctor_id;
        const matchesExecutive = !filters.executive_id ||
            coupon.registered_by?._id === filters.executive_id;
        return matchesText && matchesDoctor && matchesExecutive;
    }), [coupons, filters]);

    const change = ({ target: { name, value } }) =>
        setFilters(current => ({ ...current, [name]: value }));

    return (
        <Container maxWidth="xl" sx={{ mt: '90px', mb: 5 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Códigos médicos</Typography>
                    <Typography color="text.secondary">
                        {filtered.length} de {coupons.length} códigos BTL y cupones médicos históricos
                    </Typography>
                </Box>
                <Button
                    component={Link}
                    to="/nefro-admin/pedidos-medicos"
                    variant="contained"
                    startIcon={<ReceiptLongIcon />}
                >
                    Ver pedidos atribuidos
                </Button>
            </Stack>

            <Alert severity="info" sx={{ mb: 3 }}>
                Los códigos médicos BTL son administrados exclusivamente desde Odoo 15 y están disponibles aquí sólo para consulta.
            </Alert>

            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth size="small" label="Buscar cupón"
                        name="search" value={filters.search} onChange={change}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Médico</InputLabel>
                        <Select label="Médico" name="doctor_id" value={filters.doctor_id} onChange={change}>
                            <MenuItem value="">Todos los médicos</MenuItem>
                            {doctors.map(doctor => (
                                <MenuItem key={doctor._id} value={doctor._id}>
                                    {doctor.name?.firstName} {doctor.name?.lastName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Ejecutivo</InputLabel>
                        <Select label="Ejecutivo" name="executive_id" value={filters.executive_id} onChange={change}>
                            <MenuItem value="">Todos los ejecutivos</MenuItem>
                            {executives.map(executive => (
                                <MenuItem key={executive._id} value={executive._id}>
                                    {executive.fullname || executive.username || executive.email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}
            {!loading && failed && <Alert severity="error">No fue posible cargar los cupones médicos.</Alert>}
            {!loading && !failed && filtered.length === 0 &&
                <Alert severity="info">No hay cupones que coincidan con los filtros.</Alert>}
            <Grid container spacing={3}>
                {filtered.map(coupon => (
                    <Grid item xs={12} md={6} lg={4} key={coupon._id}>
                        <CouponCard coupon={coupon} readOnly />
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
};

export default MedicalCoupons;
